import { useTranslation } from '../../../i18n';
import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    InputNumber,
    message,
    Popconfirm,
    Switch,
    Alert,
    Tabs,
    Tag,
    Typography,
    Divider,
    Select,
    Checkbox,
    Descriptions,
    Tooltip
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    LockOutlined,
    SafetyOutlined,
    ThunderboltOutlined,
    QuestionCircleOutlined
} from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';
import TagsInput, { isIpOrCidr } from '../../../components/TagsInput';

const { TabPane } = Tabs;
const { Paragraph, Text } = Typography;
const { Option } = Select;

const SQL_PRESETS = [
    {
        name: 'Common SQL keywords',
        pattern: ".*[; ]?((or)|(insert)|(select)|(union)|(update)|(delete)|(replace)|(create)|(drop)|(alter)|(grant)|(load)|(show)|(exec))[\\s(]",
        description: 'Block common SQL keywords followed by a space or parenthesis.'
    },
    {
        name: 'Script extensions',
        pattern: ".*\\.(php|jsp|asp|aspx)$",
        description: 'Match specific script extensions in uploads or path parameters.'
    }
];

const SecuritySettings = ({ vhostId, vhostName }) => {
    const { t } = useTranslation();
    const [banIpList, setBanIpList] = useState([]);
    const [banIpEnabled, setBanIpEnabled] = useState(false);
    const [antiCC, setAntiCC] = useState(null);
    const [antiSQL, setAntiSQL] = useState(false);
    const [antiSQLRules, setAntiSQLRules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [antiSQLRuleLoading, setAntiSQLRuleLoading] = useState(false);
    const [ipModalVisible, setIpModalVisible] = useState(false);
    const [ccModalVisible, setCCModalVisible] = useState(false);
    const [sqlModalVisible, setSqlModalVisible] = useState(false);
    const [ipForm] = Form.useForm();
    const [ccForm] = Form.useForm();
    const [sqlForm] = Form.useForm();

    useEffect(() => {
        if (!vhostName) {
            return;
        }
        loadBanIPList();
        loadAntiCC();
        loadAntiSQL();
    }, [vhostName]);

    // ==== IP封禁管理 ====
    const loadBanIPList = async () => {
        try {
            const response = await hostSharedService.getBanIP(vhostName);
            if (response.success) {
                const d = response.data || {};
                setBanIpList(Array.isArray(d.rules) ? d.rules : (Array.isArray(d) ? d : []));
                setBanIpEnabled(!!d.enabled);
            }
        } catch (error) {
            console.error('加载IP封禁列表失败:', error);
        }
    };

    const handleAddBanIP = async (values) => {
        try {
            const response = await hostSharedService.addBanIP(vhostName, values.ip.trim());
            if (response.success) {
                message.success(t('IP block added'));
                setIpModalVisible(false);
                ipForm.resetFields();
                loadBanIPList();
            } else {
                message.error(response.error || response.message || t('Failed to add'));
            }
        } catch (error) {
            message.error(error.response?.data?.error || t('Failed to add'));
        }
    };

    const handleDeleteBanIP = async (ip) => {
        try {
            const response = await hostSharedService.deleteBanIP(vhostName, ip);
            if (response.success) {
                message.success(t('IP block removed'));
                loadBanIPList();
            } else {
                message.error(response.message || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed'));
        }
    };

    const handleToggleBanIP = async (enabled) => {
        try {
            const response = await hostSharedService.toggleBanIP(vhostName, enabled);
            if (response.success) {
                message.success(t('IP blocking {{status}}', { status: enabled ? t('Enable') : t('Disable') }));
                loadBanIPList();
            } else {
                message.error(response.error || response.message || t('Operation failed'));
            }
        } catch (error) {
            message.error(t('Operation failed'));
        }
    };

    // ==== Anti-CC防护 ====
    const loadAntiCC = async () => {
        try {
            const response = await hostSharedService.getAntiCC(vhostName);
            if (response.success) {
                const payload = response.data || {};
                setAntiCC(payload.enabled ? payload : null);
                ccForm.setFieldsValue({
                    limit: parseInt(payload.request, 10) || 10,
                    period: parseInt(payload.second, 10) || 60,
                    mode: payload.mode === 'vcode' ? 'vcode' : 'redirect',
                    whiteip: payload.whiteip ? payload.whiteip.replace(/\|/g, '\n') : '',
                    whiteext: payload.whiteext || ''
                });
            }
        } catch (error) {
            console.error('加载Anti-CC配置失败:', error);
        }
    };

    const handleSetAntiCC = async (values) => {
        try {
            const payload = {
                request: values.limit,
                second: values.period,
                mode: values.mode || 'redirect',
                whiteip: (values.whiteip || '').trim(),
                whiteext: (values.whiteext || '').trim()
            };
            const response = await hostSharedService.setAntiCC(
                vhostName,
                payload
            );
            if (response.success) {
                message.success(t('Anti-CC settings saved'));
                setCCModalVisible(false);
                loadAntiCC();
            } else {
                message.error(response.message || t('Failed to save'));
            }
        } catch (error) {
            message.error(t('Failed to save'));
        }
    };

    const handleDeleteAntiCC = async () => {
        try {
            const response = await hostSharedService.deleteAntiCC(vhostName);
            if (response.success) {
                message.success(t('Anti-CC protection disabled'));
                loadAntiCC();
            } else {
                message.error(response.message || t('Operation failed'));
            }
        } catch (error) {
            message.error(t('Operation failed'));
        }
    };

    // ==== Anti-SQL注入防护 ====
    const loadAntiSQL = async () => {
        try {
            const response = await hostSharedService.getAntiSQL(vhostName);
            if (response.success) {
                setAntiSQL(response.data?.enabled || false);
                setAntiSQLRules(response.data?.rules || []);
            }
        } catch (error) {
            console.error('加载Anti-SQL配置失败:', error);
        }
    };

    const handleToggleAntiSQL = async (enabled) => {
        try {
            const response = enabled
                ? await hostSharedService.enableAntiSQL(vhostName)
                : await hostSharedService.disableAntiSQL(vhostName);

            if (response.success) {
                message.success(t('SQL injection protection: {{status}}', { status: enabled ? t('Enable') : t('Disable') }));
                setAntiSQL(enabled);
                loadAntiSQL();
            } else {
                message.error(response.message || t('Operation failed'));
            }
        } catch (error) {
            message.error(t('Operation failed'));
        }
    };

    const handleAddAntiSQLRule = async (values) => {
        const rulePayload = {
            value: values.pattern,
            charset: (values.charset || 'utf-8').trim(),
            get: values.getFlag ? 1 : 0,
            post: values.postFlag ? 1 : 0
        };

        if (!rulePayload.get && !rulePayload.post) {
            message.error(t('Please enable at least GET or POST'));
            return;
        }

        setAntiSQLRuleLoading(true);
        try {
            const response = await hostSharedService.addAntiSQLRule(vhostName, rulePayload);
            if (response.success) {
                message.success(t('Rule added'));
                setSqlModalVisible(false);
                sqlForm.resetFields();
                loadAntiSQL();
            } else {
                message.error(response.message || t('Failed to add'));
            }
        } catch (error) {
            message.error(t('Failed to add: {{message}}', { message: error.message }));
        } finally {
            setAntiSQLRuleLoading(false);
        }
    };

    const handleDeleteAntiSQLRule = async (ruleId) => {
        setAntiSQLRuleLoading(true);
        try {
            const response = await hostSharedService.deleteAntiSQLRule(vhostName, ruleId);
            if (response.success) {
                message.success(t('Rule deleted'));
                loadAntiSQL();
            } else {
                message.error(response.message || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed: {{message}}', { message: error.message }));
        } finally {
            setAntiSQLRuleLoading(false);
        }
    };

    // 表格列定义
    const banIpColumns = [
        {
            title: t('IP address'),
            dataIndex: 'ip',
            key: 'ip'
        },
        {
            title: t('Expires'),
            dataIndex: 'expire',
            key: 'expire',
            render: (exp) => (exp > 0 ? new Date(exp * 1000).toLocaleString() : '-')
        },
        {
            title: t('Actions'),
            key: 'action',
            render: (_, record) => (
                <Popconfirm
                    title={t('Delete this IP block?')}
                    onConfirm={() => handleDeleteBanIP(record.ip)}
                >
                    <Button size="small" danger icon={<DeleteOutlined />}>
                        {t('Delete')}
                    </Button>
                </Popconfirm>
            )
        }
    ];

    const antiSqlColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60
        },
        {
            title: t('Rule content'),
            dataIndex: 'value',
            key: 'value',
            render: (value) => <Paragraph copyable={{ text: value || '' }} style={{ margin: 0 }}>{value}</Paragraph>
        },
        {
            title: t('Encoding'),
            dataIndex: 'charset',
            key: 'charset',
            width: 120,
            render: (text) => text || 'utf-8'
        },
        {
            title: 'GET',
            dataIndex: 'get',
            key: 'get',
            width: 80,
            render: (flag) => flag ? <Tag color="green">{t('Check')}</Tag> : <Tag>{t('Skip')}</Tag>
        },
        {
            title: 'POST',
            dataIndex: 'post',
            key: 'post',
            width: 80,
            render: (flag) => flag ? <Tag color="green">{t('Check')}</Tag> : <Tag>{t('Skip')}</Tag>
        },
        {
            title: t('Actions'),
            key: 'action',
            render: (_, record) => (
                <Popconfirm
                    title={t('Delete this rule?')}
                    onConfirm={() => handleDeleteAntiSQLRule(record.id)}
                >
                    <Button size="small" danger icon={<DeleteOutlined />}>
                        {t('Delete')}
                    </Button>
                </Popconfirm>
            )
        }
    ];

    return (
        <Tabs defaultActiveKey="banip">
            <TabPane
                tab={
                    <span>
                        <LockOutlined />
                        {t('IP blocking')}
                    </span>
                }
                key="banip"
            >
                <Card
                    title={t('IP block list')}
                    extra={
                        <Space>
                        <Switch
                            checked={banIpEnabled}
                            onChange={handleToggleBanIP}
                            checkedChildren={t('Enable')}
                            unCheckedChildren={t('Disable')}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setIpModalVisible(true)}
                        >
                            {t('Add IP block')}
                        </Button>
                        </Space>
                    }
                >
                    <Alert
                        message={t('IP blocking guide')}
                        description={t('Block specified IP addresses from accessing your website. Supports individual IPs or CIDR ranges.')}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    <Table
                        dataSource={banIpList}
                        columns={banIpColumns}
                        rowKey="id"
                        loading={loading}
                    />
                </Card>

                <Modal
                    title={t('Add IP block')}
                    open={ipModalVisible}
                    onCancel={() => {
                        setIpModalVisible(false);
                        ipForm.resetFields();
                    }}
                    onOk={() => ipForm.submit()}
                >
                    <Form form={ipForm} onFinish={handleAddBanIP} layout="vertical">
                        <Form.Item
                            name="ip"
                            label={t('IP address')}
                            rules={[{ required: true, message: t('Please enter an IP address') }]}
                            extra={t('Supports a single IP (e.g., 192.168.1.1) or CIDR range (e.g., 192.168.1.0/24)')}
                        >
                            <Input placeholder="192.168.1.1" />
                        </Form.Item>

                    </Form>
                </Modal>
            </TabPane>

            <TabPane
                tab={
                    <span>
                        <ThunderboltOutlined />
                        {t('Anti-CC protection')}
                    </span>
                }
                key="anticc"
            >
                <Card>
                    <Alert
                        message={t('Anti-CC protection guide')}
                        description={t('Limit requests from the same IP within a fixed time window, with configurable IP/extension allowlists.')}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    {antiCC ? (
                        <>
                            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
                                <Descriptions.Item label={t('Request limits')}>
                                    {t("{{value}} times", { value: antiCC.request || 0 })}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Time window')}>
                                    {t("{{value}} seconds", { value: antiCC.second || 0 })}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Allowlisted IPs')} span={2}>
                                    <Paragraph ellipsis={{ rows: 3, expandable: true }}>
                                        {antiCC.whiteip ? antiCC.whiteip.replace(/\|/g, '\n') : t('None')}
                                    </Paragraph>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Allowed extensions')}>
                                    {antiCC.whiteext || t('None')}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Action mode')}>
                                    {antiCC.mode === 'vcode' ? t('Image captcha') : t('Redirect')}
                                </Descriptions.Item>
                            </Descriptions>
                            <Space>
                                <Button
                                    onClick={() => {
                                        ccForm.setFieldsValue({
                                            limit: parseInt(antiCC.request, 10) || 10,
                                            period: parseInt(antiCC.second, 10) || 60,
                                            mode: antiCC.mode === 'vcode' ? 'vcode' : 'redirect',
                                            whiteip: antiCC.whiteip ? antiCC.whiteip.replace(/\|/g, '\n') : '',
                                            whiteext: antiCC.whiteext || ''
                                        });
                                        setCCModalVisible(true);
                                    }}
                                >
                                    {t('Edit configuration')}
                                </Button>
                                <Popconfirm
                                    title={t('Turn off Anti-CC protection?')}
                                    onConfirm={handleDeleteAntiCC}
                                >
                                    <Button danger>{t('Disable protection')}</Button>
                                </Popconfirm>
                            </Space>
                        </>
                    ) : (
                        <Button
                            type="primary"
                            onClick={() => {
                                ccForm.setFieldsValue({ limit: 10, period: 60, mode: 'redirect', whiteip: '', whiteext: '' });
                                setCCModalVisible(true);
                            }}
                        >
                            {t('Enable Anti-CC protection')}
                        </Button>
                    )}
                </Card>

            <Modal
                title={t('Configure Anti-CC protection')}
                open={ccModalVisible}
                onCancel={() => setCCModalVisible(false)}
                onOk={() => ccForm.submit()}
            >
                <Form form={ccForm} onFinish={handleSetAntiCC} layout="vertical">
                    <Form.Item
                        name="limit"
                        label={t('Visit limit')}
                        rules={[{ required: true, message: t('Enter the number of requests') }]}
                    >
                        <InputNumber min={1} max={100000} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="period"
                        label={t('Interval (seconds)')}
                        rules={[{ required: true, message: t('Please enter a time interval') }]}
                    >
                        <InputNumber min={1} max={3600} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="mode"
                        label={t('Action')}
                    >
                        <Select>
                            <Option value="redirect">{t('Redirect')}</Option>
                            <Option value="vcode">{t('Image captcha')}</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="whiteip"
                        label={t('Allowlisted IPs')}
                        extra={t('Enter an IP or CIDR, e.g., 1.2.3.4 or 1.2.3.0/24.')}
                    >
                        <TagsInput format="lines" placeholder="192.168.0.1, 10.0.0.0/8" validate={(v) => (isIpOrCidr(v) ? null : t('Invalid IP / CIDR'))} />
                    </Form.Item>
                    <Form.Item
                        name="whiteext"
                        label={t('Allowed extensions (separated by |)')}
                        extra={t('Enter extensions without a leading asterisk, e.g., cmd|exe|php')}
                    >
                        <Input placeholder={t('E.g. php|asp')} />
                    </Form.Item>
                    <Alert
                        type="info"
                        showIcon
                        message={t('Notice')}
                        description={t('Allowlisted IPs and extensions are appended to the rule. Keep the formatting clean and separate multiple entries with newlines or |.')}
                        style={{ marginBottom: 0 }}
                    />
                </Form>
            </Modal>

            <Modal
                title={t('Add Anti-SQL rule')}
                open={sqlModalVisible}
                onCancel={() => setSqlModalVisible(false)}
                onOk={() => sqlForm.submit()}
                confirmLoading={antiSQLRuleLoading}
            >
                <Form form={sqlForm} onFinish={handleAddAntiSQLRule} layout="vertical">
                    <Form.Item label={t('Preset rules')}>
                        <Space wrap>
                            {SQL_PRESETS.map((preset) => (
                                <Tooltip key={preset.name} title={t(preset.description)}>
                                    <Button
                                        type="link"
                                        onClick={() => sqlForm.setFieldsValue({ pattern: preset.pattern })}
                                    >
                                        {t(preset.name)}
                                    </Button>
                                </Tooltip>
                            ))}
                        </Space>
                    </Form.Item>
                    <Form.Item
                        name="pattern"
                        label={
                            <span>
                                {t('Match regex')}
                                <Tooltip title={t('PCRE regular expression. Matching parameter values will be blocked. Please ensure legitimate data is not blocked.')}>
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            </span>
                        }
                        rules={[{ required: true, message: t('Please enter a match regex') }]}
                    >
                        <Input.TextArea
                            rows={3}
                            placeholder={t('E.g. .*[; ]?(or|union|select|insert|delete)[\\s(]')}
                        />
                    </Form.Item>
                    <Form.Item
                        name="charset"
                        label={t('Character encoding')}
                        initialValue="utf-8"
                    >
                        <Input placeholder="utf-8" />
                    </Form.Item>
                    <Form.Item name="getFlag" valuePropName="checked">
                        <Checkbox>{t('Check GET parameters')}</Checkbox>
                    </Form.Item>
                    <Form.Item name="postFlag" valuePropName="checked">
                        <Checkbox>{t('Check POST parameters')}</Checkbox>
                    </Form.Item>
                    <Alert
                        type="info"
                        showIcon
                        message={t('Description')}
                        description={t('Combine multiple keywords using | or more advanced regular expressions. Matching requests are rejected immediately.')}
                    />
                </Form>
            </Modal>
            </TabPane>

            <TabPane
                tab={
                    <span>
                        <SafetyOutlined />
                        {t('SQL injection protection')}
                    </span>
                }
                key="antisql"
            >
                <Card>
                    <Alert
                        message={t('SQL injection protection guide')}
                        description={t('Detect dangerous keywords in parameter values using regular expressions to block injection requests.')}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    <Space style={{ marginBottom: 16, alignItems: 'center' }}>
                        <p style={{ margin: 0 }}>
                            {t('Status:')}
                            <Tag color={antiSQL ? 'green' : 'default'} style={{ marginLeft: 8 }}>
                                {antiSQL ? t('Enabled') : t('Disabled')}
                            </Tag>
                        </p>
                        <Switch
                            checked={antiSQL}
                            onChange={handleToggleAntiSQL}
                            checkedChildren={t('Enable')}
                            unCheckedChildren={t('Disable')}
                        />
                        <Button
                            type="primary"
                            onClick={() => {
                                sqlForm.resetFields();
                                sqlForm.setFieldsValue({ charset: 'utf-8', getFlag: true, postFlag: true });
                                setSqlModalVisible(true);
                            }}
                        >
                            {t('Add rule')}
                        </Button>
                    </Space>
                    <Alert
                        type="info"
                        showIcon
                        message={t('Example regex')}
                        description={
                            <Paragraph code style={{ margin: 0, wordBreak: 'break-all' }}>
                                .*[; ]?((or)|(insert)|(select)|(union)|(update)|(delete)|(replace)|(create)|(drop)|(alter)|(grant)|(load)|(show)|(exec))[\s(]
                            </Paragraph>
                        }
                        style={{ marginBottom: 16 }}
                    />
                    <Table
                        columns={antiSqlColumns}
                        dataSource={antiSQLRules}
                        rowKey="id"
                        loading={antiSQLRuleLoading}
                        pagination={false}
                        locale={{ emptyText: t('No rules yet') }}
                    />
                </Card>
            </TabPane>
        </Tabs>
    );
};

export default SecuritySettings;
